type AddressSpace = "local" | "loopback";
type LocalNetworkRequestInit = RequestInit & { targetAddressSpace?: AddressSpace };

function stripIpv6Brackets(hostname: string): string {
  return hostname.startsWith("[") && hostname.endsWith("]")
    ? hostname.slice(1, -1)
    : hostname;
}

function ipv4AddressSpace(hostname: string): AddressSpace | null {
  const octets = hostname.split(".").map(Number);
  if (
    octets.length !== 4 ||
    octets.some((part) => !Number.isInteger(part) || part < 0 || part > 255)
  ) {
    return null;
  }
  if (octets[0] === 127) return "loopback";
  if (
    octets[0] === 10 ||
    (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31) ||
    (octets[0] === 192 && octets[1] === 168) ||
    (octets[0] === 169 && octets[1] === 254)
  ) {
    return "local";
  }
  return null;
}

function targetAddressSpace(url: URL): AddressSpace | null {
  const hostname = stripIpv6Brackets(url.hostname.toLowerCase());
  if (
    hostname === "localhost" ||
    hostname.endsWith(".localhost") ||
    hostname === "::1"
  ) {
    return "loopback";
  }
  const ipv4 = ipv4AddressSpace(hostname);
  if (ipv4) return ipv4;
  if (
    hostname.endsWith(".local") ||
    hostname.endsWith(".home.arpa") ||
    /^(fc|fd|fe[89ab])/i.test(hostname)
  ) {
    return "local";
  }

  // From an HTTPS deployment, public HTTP is already blockable mixed content.
  // Marking an HTTP hostname as local also supports names such as
  // charts.boat.internal that resolve to an RFC1918 address. A browser checks
  // the actual destination before granting access.
  if (location.protocol === "https:" && url.protocol === "http:") return "local";
  return null;
}

/**
 * CORS fetch for user-provided chart servers. The extra address-space hint is
 * ignored by older engines and enables Chromium's permission-gated HTTPS to
 * HTTP local-network exception where supported.
 */
export function fetchChart(
  input: string | URL,
  init: RequestInit = {},
): Promise<Response> {
  const url = input instanceof URL ? input : new URL(input, document.baseURI);
  const request: LocalNetworkRequestInit = {
    ...init,
    mode: init.mode ?? "cors",
    credentials: init.credentials ?? "omit",
  };
  const addressSpace = targetAddressSpace(url);
  if (addressSpace) request.targetAddressSpace = addressSpace;
  return fetch(url, request);
}
